import React from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Database, Lock, CreditCard, Cookie, UserCog, Mail, Baby, RefreshCw } from "lucide-react";
import MarketingLayout from "@/components/MarketingLayout";

function Section({ icon: Icon, title, children }) {
  return (
    <div className="ss-card" style={{ padding: 24, marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span style={{ width: 38, height: 38, borderRadius: 10, background: "var(--brand)", color: "#fff", display: "grid", placeItems: "center", flexShrink: 0 }}>
          <Icon size={19} />
        </span>
        <h2 style={{ fontSize: 20, fontWeight: 800 }}>{title}</h2>
      </div>
      <div style={{ color: "var(--muted)", fontSize: 15, lineHeight: 1.7 }}>{children}</div>
    </div>
  );
}

export default function Privacy() {
  const navigate = useNavigate();
  const updated = "June 2026";
  return (
    <MarketingLayout>
      <div className="fade-up" data-testid="privacy-page">
        {/* Hero */}
        <div className="ss-card" style={{ padding: 34, background: "var(--brand)", color: "#fff" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,.14)", borderRadius: 999, padding: "6px 14px", fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
            <ShieldCheck size={14} style={{ color: "var(--gold)" }} /> Your privacy matters
          </div>
          <h1 style={{ fontSize: 34, fontWeight: 800, marginTop: 16, lineHeight: 1.2 }}>Privacy Policy</h1>
          <p style={{ marginTop: 14, fontSize: 16, lineHeight: 1.7, color: "rgba(255,255,255,.9)", maxWidth: 640 }}>
            SouqStream is committed to protecting your personal information. This policy explains what we collect,
            how we use it, and the choices you have. By using SouqStream you agree to the practices described here.
          </p>
          <div style={{ marginTop: 14, fontSize: 13, color: "rgba(255,255,255,.8)" }}>Last updated: {updated}</div>
        </div>

        <Section icon={Database} title="Information we collect">
          <p>We collect information you provide directly and information generated as you use the platform:</p>
          <ul style={{ margin: "10px 0 0", paddingLeft: 20 }}>
            <li><b>Account details</b> — name, email, password (stored hashed), and profile or store information.</li>
            <li><b>Seller & payout details</b> — business information and bank/payout data you connect through Stripe.</li>
            <li><b>Orders & activity</b> — purchases, bids, donations, saved items, messages, and shipping addresses.</li>
            <li><b>Technical data</b> — device, browser, IP address, and usage logs to keep the service secure and reliable.</li>
          </ul>
        </Section>

        <Section icon={UserCog} title="How we use your information">
          <p>We use your information to operate and improve SouqStream, including to:</p>
          <ul style={{ margin: "10px 0 0", paddingLeft: 20 }}>
            <li>Create and manage your account and seller profile.</li>
            <li>Process orders, auctions, donations, payments, refunds, and shipping.</li>
            <li>Enable live shows, chat, bids, and real-time notifications.</li>
            <li>Send transactional emails (order, refund, payout, and account messages) and, with consent, updates.</li>
            <li>Detect fraud, enforce our terms, and comply with legal obligations.</li>
          </ul>
        </Section>

        <Section icon={CreditCard} title="Payments">
          <p>
            Payments and seller payouts are processed by <b>Stripe</b>. When you pay or connect a payout account,
            your card and banking details are handled directly by Stripe under their own security and privacy
            standards — SouqStream never stores full card or bank numbers. See{" "}
            <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer" style={{ color: "var(--brand)", fontWeight: 700 }}>Stripe's Privacy Policy</a>.
          </p>
        </Section>

        <Section icon={Lock} title="How we share information">
          <p>
            We do not sell your personal information. We share it only with service providers who help us run the
            platform — payment processing (Stripe), shipping carriers, and email delivery (Brevo) — and when required
            by law. Sellers and buyers see limited information needed to complete a transaction (for example, a
            shipping name and address, or a store name).
          </p>
        </Section>

        <Section icon={Cookie} title="Cookies & local storage">
          <p>
            We use cookies and browser local storage to keep you signed in, remember your cart and preferences, and
            understand how the platform is used so we can improve it. You can control cookies through your browser
            settings; disabling them may limit some features.
          </p>
        </Section>

        <Section icon={ShieldCheck} title="Data security & retention">
          <p>
            We protect your data with encryption in transit, hashed passwords, and access controls. No system is
            perfectly secure, but we work hard to safeguard your information. We keep personal data only as long as
            needed to provide the service and meet legal, tax, and accounting requirements.
          </p>
        </Section>

        <Section icon={UserCog} title="Your rights & choices">
          <p>
            Depending on your location, you may have the right to access, correct, export, or delete your personal
            information, and to object to or restrict certain processing. You can update most details in your profile,
            unsubscribe from marketing emails at any time, or contact us to exercise these rights.
          </p>
        </Section>

        <Section icon={Baby} title="Children's privacy">
          <p>
            SouqStream is not directed to children under 13 (or the minimum age in your region), and we do not
            knowingly collect their personal information. If you believe a child has provided us data, please contact us.
          </p>
        </Section>

        <Section icon={RefreshCw} title="Changes to this policy">
          <p>
            We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated" date
            above and, where appropriate, notify you. Continued use of SouqStream after changes means you accept the
            updated policy.
          </p>
        </Section>

        <Section icon={Mail} title="Contact us">
          <p>
            Questions about this policy or your data? Reach us at{" "}
            <a href="mailto:souqstream@gmail.com" style={{ color: "var(--brand)", fontWeight: 700 }}>souqstream@gmail.com</a>{" "}
            or through our{" "}
            <span onClick={() => navigate("/contact")} style={{ color: "var(--brand)", fontWeight: 700, cursor: "pointer" }} data-testid="privacy-contact-link">Contact page</span>.
          </p>
        </Section>
      </div>
    </MarketingLayout>
  );
}
